import React, { useState, useMemo, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  FiltersRow,
  FilterGroup,
  FilterLabel,
  InlineInput,
  SaveBtn,
} from "../styles";
import TagMultiSelect from "./TagMultiSelect";
import ExcelUploadPanel from "./ExcelUploadPanel";
import TriggerBreachScheduleModal from "./TriggerBreachScheduleModal";
import KpiCascadeFilter, { kpiMatchesCascadeSteps } from "../../../DataManagment/Quantative/component/KpiTagFilter";
import { PERIOD_TYPE_LABELS } from "../hooks/useTriggers";

// ─── Styles ───────────────────────────────────────────────────────────────────
const Wrapper = styled.div``;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const ExcelBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #fff;
  color: #3f88a5;
  border: 1.5px solid #3f88a5;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  margin-left: auto;
  &:hover { background: #e0f2fe; }
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  background: #3f88a5;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #2e6d88; }
`;

const FormCard = styled.div`
  margin-top: 20px;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px 20px 24px;
  background: #fff;
`;

const StepLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: #3f88a5;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 10px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f1f5f9;
  margin: 18px 0;
`;

const StepRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const StepCol = styled.div`
  flex: 1 1 220px;
  min-width: 180px;
`;

const ColHeaderCell = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const EditIconBtn = styled.button`
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  padding: 2px 6px;
  color: #64748b;
  font-size: 11px;
  line-height: 1;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  &:hover { color: #3f88a5; border-color: #3f88a5; background: #f0f9ff; }
  ${({ $active }) => $active && `
    color: #3f88a5;
    border-color: #3f88a5;
    background: #e0f2fe;
  `}
`;

const HeaderDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  padding: 10px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 220px;
  text-transform: none;
  font-weight: 400;
  letter-spacing: normal;
`;

const DropdownActions = styled.div`
  display: flex;
  gap: 6px;
`;

const ApplyAllBtn = styled.button`
  padding: 7px 14px;
  background: #f1f5f9;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #3f88a5;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  &:hover { background: #e0f2fe; border-color: #3f88a5; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ScrollableTable = styled.div`
  margin-top: 20px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  max-height: 340px;
  overflow-y: scroll;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #f8fafc; border-radius: 10px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Thead = styled.thead`
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Th = styled.th`
  padding: 9px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 1.5px solid #e2e8f0;
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:not(:last-child) td { border-bottom: 1px solid #f1f5f9; }
`;

const Td = styled.td`
  padding: 8px 12px;
  color: #1e293b;
  vertical-align: middle;
`;

const ExistingBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  background: #dcfce7;
  color: #166534;
  padding: 2px 7px;
  border-radius: 20px;
  margin-left: 6px;
`;

const ValidationMsg = styled.p`
  font-size: 12px;
  color: #ef4444;
  margin: 8px 0 0;
`;

const SuccessMsg = styled.p`
  font-size: 12px;
  color: #16a34a;
  margin: 8px 0 0;
`;

const CancelBtn = styled.button`
  padding: 8px 18px;
  background: transparent;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: #94a3b8; color: #1e293b; }
`;

// ─── Component ────────────────────────────────────────────────────────────────
const ConfigureTab = ({ hook }) => {
  const {
    sources,
    tags,
    triggers,
    questions,
    financialYears,
    selectedFinancialYear,
    handleFinancialYearChange,
    cfg_periodType, setCfg_periodType,
    cfg_selectedPeriods, setCfg_selectedPeriods,
    cfg_sourceIds, setCfg_sourceIds,
    cfg_questionIds, setCfg_questionIds,
    cfg_commonMin, setCfg_commonMin,
    cfg_commonMax, setCfg_commonMax,
    bulkCreateTriggers,
    getTriggers,
    computePeriodSlots,
    resolveBaseUnit,
    resolveBaseUnitId,
  } = hook;

  const [filterSteps, setFilterSteps]     = useState([]);
  const [formOpen, setFormOpen]           = useState(false);
  const [showExcel, setShowExcel]         = useState(false);
  const [showSchedule, setShowSchedule]   = useState(false);
  const [saving, setSaving]               = useState(false);
  const [saveProgress, setSaveProgress]   = useState(0);
  const [validationMsg, setValidationMsg] = useState("");
  const [successMsg, setSuccessMsg]       = useState("");
  const [rowOverrides, setRowOverrides]   = useState({});
  const [selectedKeys, setSelectedKeys]   = useState(new Set());
  const selectAllRef = useRef(null);
  const didAutoSelectKpisRef = useRef(false);
  const [editingMin, setEditingMin] = useState(false);
  const [editingMax, setEditingMax] = useState(false);
  const minCellRef = useRef(null);
  const maxCellRef = useRef(null);

  useEffect(() => {
    if (!editingMin && !editingMax) return;
    const handler = e => {
      if (editingMin && minCellRef.current && !minCellRef.current.contains(e.target)) {
        setEditingMin(false);
      }
      if (editingMax && maxCellRef.current && !maxCellRef.current.contains(e.target)) {
        setEditingMax(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [editingMin, editingMax]);

  // Auto-select all KPIs after a location is picked; clear them when none are picked.
  useEffect(() => {
    if (cfg_sourceIds.length === 0) {
      didAutoSelectKpisRef.current = false;
      setCfg_questionIds([]);
      return;
    }
    if (didAutoSelectKpisRef.current || questions.length === 0) return;
    setCfg_questionIds(questions.map(q => q.questionId));
    didAutoSelectKpisRef.current = true;
  }, [cfg_sourceIds, questions, setCfg_questionIds]);

  // Deselecting all periods also clears locations (which in turn clears KPIs).
  useEffect(() => {
    if (cfg_selectedPeriods.length === 0 && cfg_sourceIds.length > 0) {
      setCfg_sourceIds([]);
    }
  }, [cfg_selectedPeriods, cfg_sourceIds, setCfg_sourceIds]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const periodSlots = useMemo(
    () => computePeriodSlots(cfg_periodType),
    [computePeriodSlots, cfg_periodType]
  );

  const kpiListForFilter = useMemo(
    () => questions.map(q => ({ ...q, id: q.questionId, rawTags: q.tags })),
    [questions]
  );

  const filteredQuestions = useMemo(() => {
    const active = filterSteps.filter(s => s.values?.size);
    if (!active.length) return questions;
    return kpiListForFilter.filter(q => kpiMatchesCascadeSteps(q, filterSteps));
  }, [questions, kpiListForFilter, filterSteps]);

  const selectedQuestions = useMemo(
    () => questions.filter(q => cfg_questionIds.includes(q.questionId)),
    [questions, cfg_questionIds]
  );

  const selectedSources = useMemo(
    () => sources.filter(s => cfg_sourceIds.includes(s.id)),
    [sources, cfg_sourceIds]
  );

  // One row per selected question; merge existing trigger values across all
  // selected (source × period) combinations for display.
  const questionRows = useMemo(() => {
    return selectedQuestions.map(q => {
      const key = String(q.questionId);

      // All triggers for this KPI in this financial year — no period/source filter.
      // Used to detect if a trigger exists for a DIFFERENT period than what's selected.
      const allKpiTriggers = triggers.filter(
        t =>
          t.questionId === q.questionId &&
          String(t.financialYearId) === String(selectedFinancialYear)
      );

      // Triggers that match the currently selected periods (and sources).
      const existingTriggers = allKpiTriggers.filter(
        t =>
          (cfg_selectedPeriods.length === 0 ||
            cfg_selectedPeriods.some(p => p.fromDate === t.fromDate && p.toDate === t.toDate)) &&
          (cfg_sourceIds.length === 0 || cfg_sourceIds.includes(t.sourceId))
      );

      // Disabled only when a trigger exists for a DIFFERENT period than the current selection.
      // i.e. trigger exists somewhere, but NOT in the selected periods → can't create a new one here.
      // If the trigger IS in the selected periods the user can edit it.
      const isDisabled =
        allKpiTriggers.length > 0 && existingTriggers.length === 0;

      const mins  = [...new Set(existingTriggers
        .map(t => t.minTargetData)
        .filter(v => v !== null && v !== undefined && v !== ""))];
      const maxes = [...new Set(existingTriggers
        .map(t => t.maxTargetData)
        .filter(v => v !== null && v !== undefined && v !== ""))];
      return {
        q,
        key,
        hasExisting: existingTriggers.length > 0,
        isDisabled,
        mergedMin: mins.length === 1 ? String(mins[0]) : "",
        mergedMax: maxes.length === 1 ? String(maxes[0]) : "",
      };
    });
  }, [selectedQuestions, triggers, selectedFinancialYear, cfg_selectedPeriods, cfg_sourceIds]);

  // Sync selectedKeys: new rows auto-checked, stale keys pruned.
  // Locked rows (trigger exists for a different period) are excluded.
  useEffect(() => {
    const selectableKeys = new Set(questionRows.filter(r => !r.isDisabled).map(r => r.key));
    setSelectedKeys(prev => {
      const pruned = new Set([...prev].filter(k => selectableKeys.has(k)));
      for (const k of selectableKeys) pruned.add(k);
      return pruned;
    });
  }, [questionRows]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    const selectableCount = questionRows.filter(r => !r.isDisabled).length;
    selectAllRef.current.indeterminate =
      selectedKeys.size > 0 && selectedKeys.size < selectableCount;
  }, [selectedKeys, questionRows]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getRowMin = (key, row) =>
    rowOverrides[key]?.min !== undefined ? rowOverrides[key].min : row.mergedMin;
  const getRowMax = (key, row) =>
    rowOverrides[key]?.max !== undefined ? rowOverrides[key].max : row.mergedMax;
  const handleRowChange = (key, field, value) =>
    setRowOverrides(prev => ({ ...prev, [key]: { ...(prev[key] ?? {}), [field]: value } }));

  const toggleKey = key =>
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const handleSelectAll = () => {
    const selectableKeys = questionRows.filter(r => !r.isDisabled).map(r => r.key);
    if (selectedKeys.size === selectableKeys.length) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(selectableKeys));
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePeriodTypeChange = e => {
    setCfg_periodType(e.target.value);
    setCfg_selectedPeriods([]);
  };

  const togglePeriod = fromDate => {
    setCfg_selectedPeriods(prev => {
      const exists = prev.some(p => p.fromDate === fromDate);
      if (exists) return prev.filter(p => p.fromDate !== fromDate);
      const slot = periodSlots.find(s => s.fromDate === fromDate);
      return slot ? [...prev, slot] : prev;
    });
  };

  const applyCommonToAll = field => value => {
    setRowOverrides(prev => {
      const next = { ...prev };
      for (const row of questionRows) {
        next[row.key] = { ...(next[row.key] ?? {}), [field]: value(row) };
      }
      return next;
    });
  };

  const handleApplyAllMin = () => applyCommonToAll("min")(row => cfg_commonMin || row.mergedMin);
  const handleClearAllMin = () => applyCommonToAll("min")(row => row.mergedMin);
  const handleApplyAllMax = () => applyCommonToAll("max")(row => cfg_commonMax || row.mergedMax);
  const handleClearAllMax = () => applyCommonToAll("max")(row => row.mergedMax);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!cfg_periodType)             return "Please select a period type.";
    if (!cfg_selectedPeriods.length) return "Please select at least one period.";
    if (!cfg_sourceIds.length)       return "Please select at least one location.";
    if (!cfg_questionIds.length)     return "Please select at least one KPI.";
    if (selectedKeys.size === 0)     return "Please select at least one row to save.";
    for (const row of questionRows) {
      if (!selectedKeys.has(row.key)) continue;
      const min = getRowMin(row.key, row);
      const max = getRowMax(row.key, row);
      if (min !== "" && max !== "" && Number(min) >= Number(max)) {
        return `Min must be less than max (${row.q.title}).`;
      }
    }
    return "";
  };

  // ── Save — expands each question row to all (source × period) payloads ────
  const handleSave = async () => {
    setValidationMsg("");
    setSuccessMsg("");
    const err = validate();
    if (err) { setValidationMsg(err); return; }

    const payloads = [];
    for (const row of questionRows) {
      if (!selectedKeys.has(row.key)) continue;
      const min = getRowMin(row.key, row);
      const max = getRowMax(row.key, row);
      for (const s of selectedSources) {
        for (const p of cfg_selectedPeriods) {
          payloads.push({
            financialYearId: Number(selectedFinancialYear),
            questionId:      row.q.questionId,
            questionTitle:   row.q.title,
            unitId:          resolveBaseUnitId(row.q),
            sourceId:        s.id,
            fromDate:        p.fromDate,
            toDate:          p.toDate,
            minTrigger:      String(min),
            maxTrigger:      String(max),
            questionType:    "quantitative",
            columnId:        null,
            rowId:           null,
          });
        }
      }
    }

    setSaving(true);
    setSaveProgress(0);
    const ok = await bulkCreateTriggers(payloads, setSaveProgress);
    setSaving(false);

    if (ok) {
      const kpiCount = questionRows.filter(r => selectedKeys.has(r.key)).length;
      setSuccessMsg(
        `${kpiCount} KPI${kpiCount > 1 ? "s" : ""} saved across ${payloads.length} trigger${payloads.length > 1 ? "s" : ""}.`
      );
      await getTriggers();
      handleCancel();
    } else {
      setValidationMsg("Failed to save triggers. Please try again.");
    }
  };

  const handleCancel = () => {
    setFormOpen(false);
    setValidationMsg("");
    setCfg_periodType("");
    setCfg_selectedPeriods([]);
    setCfg_sourceIds([]);
    setCfg_questionIds([]);
    setFilterSteps([]);
    setCfg_commonMin("");
    setCfg_commonMax("");
    setRowOverrides({});
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Wrapper>
      {successMsg && <SuccessMsg>{successMsg}</SuccessMsg>}

      <TopBar>
        {!formOpen && (
          <AddBtn onClick={() => { setFormOpen(true); setSuccessMsg(""); }}>
            + Add Configuration
          </AddBtn>
        )}
        <ExcelBtn onClick={() => setShowSchedule(true)}>
          ⏰ Schedule
        </ExcelBtn>
        <ExcelBtn onClick={() => setShowExcel(true)}>
          📊 Upload via Excel
        </ExcelBtn>
      </TopBar>

      <ExcelUploadPanel
        open={showExcel}
        onClose={() => setShowExcel(false)}
        hook={hook}
        onSaveSuccess={async () => {
          await getTriggers();
          handleCancel();
        }}
      />

      <TriggerBreachScheduleModal
        open={showSchedule}
        onClose={() => setShowSchedule(false)}
        hook={hook}
      />

      {formOpen && (
        <FormCard>

          {/* Step 1 — Financial Year */}
          <StepLabel>Step 1 — Financial Year</StepLabel>
          <FiltersRow>
            <FilterGroup style={{ maxWidth: 240 }}>
              <FilterLabel>Financial Year</FilterLabel>
              <select
                value={selectedFinancialYear}
                onChange={(e) => handleFinancialYearChange(e.target.value)}
                style={{
                  padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
                  fontSize: 13, color: "#1e293b", background: "#fff", outline: "none",
                  transition: "border-color 0.15s",
                }}
              >
                <option value="">Select year</option>
                {financialYears.map((fy) => (
                  <option key={fy.value} value={fy.value}>{fy.label}</option>
                ))}
              </select>
            </FilterGroup>
          </FiltersRow>

          {/* Step 2 — Period Type */}
          {selectedFinancialYear && (
            <>
              <Divider />
              <StepLabel>Step 2 — Period Type</StepLabel>
              <FiltersRow>
                <FilterGroup style={{ maxWidth: 240 }}>
                  <FilterLabel>Period Type</FilterLabel>
                  <select
                    value={cfg_periodType}
                    onChange={handlePeriodTypeChange}
                    style={{
                      padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
                      fontSize: 13, color: "#1e293b", background: "#fff", outline: "none",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <option value="">Select type</option>
                    {Object.entries(PERIOD_TYPE_LABELS).map(([val, lbl]) => (
                      <option key={val} value={val}>{lbl}</option>
                    ))}
                  </select>
                </FilterGroup>
              </FiltersRow>
            </>
          )}

          {/* Steps 3 + 4 */}
          {selectedFinancialYear && cfg_periodType && (
            <>
              <Divider />
              <StepRow>
                <StepCol>
                  <StepLabel>Step 3 — Periods</StepLabel>
                  <FilterLabel>Periods ({periodSlots.length})</FilterLabel>
                  <TagMultiSelect
                    options={periodSlots}
                    selected={cfg_selectedPeriods.map(p => p.fromDate)}
                    getKey={s => s.fromDate}
                    getLabel={s => s.label}
                    placeholder="Select periods…"
                    onToggle={togglePeriod}
                    onSelectAll={keys =>
                      setCfg_selectedPeriods(
                        keys.length === 0 ? [] : periodSlots.filter(s => keys.includes(s.fromDate))
                      )
                    }
                  />
                </StepCol>

                <StepCol>
                  <StepLabel>Step 4 — Locations</StepLabel>
                  <FilterLabel>Locations</FilterLabel>
                  <TagMultiSelect
                    options={sources}
                    selected={cfg_sourceIds}
                    getKey={s => s.id}
                    getLabel={s => s.unitCode}
                    placeholder="Select locations…"
                    onToggle={id =>
                      setCfg_sourceIds(prev =>
                        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                      )
                    }
                    onSelectAll={ids => setCfg_sourceIds(ids)}
                  />
                </StepCol>
              </StepRow>
            </>
          )}

          {/* Step 5 — KPIs */}
          {cfg_sourceIds.length > 0 && (
            <>
              <Divider />
              <StepLabel>Step 5 — KPIs</StepLabel>
              <div style={{ marginBottom: 12 }}>
                <FilterLabel>Filter by Tags</FilterLabel>
                <KpiCascadeFilter
                  kpiList={kpiListForFilter}
                  filterSteps={filterSteps}
                  onFilterStepsChange={steps => {
                    setFilterSteps(steps);
                    const active = steps.filter(s => s.values?.size);
                    const next = active.length
                      ? kpiListForFilter.filter(q => kpiMatchesCascadeSteps(q, steps))
                      : questions;
                    setCfg_questionIds(next.map(q => q.questionId));
                  }}
                />
              </div>

              <div>
                <FilterLabel>KPIs{filterSteps.some(s => s.values?.size) ? " (filtered)" : ""}</FilterLabel>
                <TagMultiSelect
                  options={filteredQuestions}
                  selected={cfg_questionIds}
                  getKey={q => q.questionId}
                  getLabel={q => q.title}
                  placeholder="Select KPIs…"
                  onToggle={id =>
                    setCfg_questionIds(prev =>
                      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                    )
                  }
                  onSelectAll={ids => setCfg_questionIds(ids)}
                />
                {filteredQuestions.length === 0 && (
                  <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                    No KPIs match the selected tags.
                  </p>
                )}
              </div>
            </>
          )}

          {/* Step 6 — Trigger Values + Save/Cancel */}
          {cfg_questionIds.length > 0 && (
            <>
              <Divider />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <StepLabel style={{ margin: 0 }}>Step 6 — Trigger Values</StepLabel>
                <div style={{ display: "flex", gap: 10 }}>
                  <CancelBtn onClick={handleCancel}>Cancel</CancelBtn>
                  <SaveBtn
                    onClick={handleSave}
                    disabled={saving || questionRows.length === 0}
                    style={{ padding: "8px 18px", fontSize: 13 }}
                  >
                    {saving ? `Saving… ${saveProgress}%` : "Save Triggers"}
                  </SaveBtn>
                </div>
              </div>

              {validationMsg && <ValidationMsg>{validationMsg}</ValidationMsg>}

              {questionRows.length > 0 && (
                <ScrollableTable>
                  <PreviewTable>
                    <Thead>
                      <tr>
                        <Th style={{ width: 36, textAlign: "center" }}>
                          <input
                            ref={selectAllRef}
                            type="checkbox"
                            checked={questionRows.filter(r => !r.isDisabled).length > 0 && selectedKeys.size === questionRows.filter(r => !r.isDisabled).length}
                            onChange={handleSelectAll}
                            style={{ cursor: "pointer", accentColor: "#3f88a5" }}
                          />
                        </Th>
                        <Th>KPI</Th>
                        <Th>Unit</Th>
                        <Th>
                          <ColHeaderCell ref={minCellRef}>
                            <span>Min Trigger</span>
                            <EditIconBtn
                              type="button"
                              $active={editingMin}
                              title="Bulk edit min"
                              onClick={() => setEditingMin(v => !v)}
                            >
                              ✎
                            </EditIconBtn>
                            {editingMin && (
                              <HeaderDropdown>
                                <InlineInput
                                  type="number"
                                  placeholder="Common min"
                                  value={cfg_commonMin}
                                  onChange={e => setCfg_commonMin(e.target.value)}
                                  autoFocus
                                />
                                <DropdownActions>
                                  <ApplyAllBtn
                                    type="button"
                                    onClick={handleApplyAllMin}
                                    disabled={!cfg_commonMin}
                                    style={{ flex: 1 }}
                                  >
                                    Apply All
                                  </ApplyAllBtn>
                                  <ApplyAllBtn
                                    type="button"
                                    onClick={handleClearAllMin}
                                    disabled={questionRows.length === 0}
                                    style={{ color: "#64748b", flex: 1 }}
                                  >
                                    Clear All
                                  </ApplyAllBtn>
                                </DropdownActions>
                              </HeaderDropdown>
                            )}
                          </ColHeaderCell>
                        </Th>
                        <Th>
                          <ColHeaderCell ref={maxCellRef}>
                            <span>Max Trigger</span>
                            <EditIconBtn
                              type="button"
                              $active={editingMax}
                              title="Bulk edit max"
                              onClick={() => setEditingMax(v => !v)}
                            >
                              ✎
                            </EditIconBtn>
                            {editingMax && (
                              <HeaderDropdown>
                                <InlineInput
                                  type="number"
                                  placeholder="Common max"
                                  value={cfg_commonMax}
                                  onChange={e => setCfg_commonMax(e.target.value)}
                                  autoFocus
                                />
                                <DropdownActions>
                                  <ApplyAllBtn
                                    type="button"
                                    onClick={handleApplyAllMax}
                                    disabled={!cfg_commonMax}
                                    style={{ flex: 1 }}
                                  >
                                    Apply All
                                  </ApplyAllBtn>
                                  <ApplyAllBtn
                                    type="button"
                                    onClick={handleClearAllMax}
                                    disabled={questionRows.length === 0}
                                    style={{ color: "#64748b", flex: 1 }}
                                  >
                                    Clear All
                                  </ApplyAllBtn>
                                </DropdownActions>
                              </HeaderDropdown>
                            )}
                          </ColHeaderCell>
                        </Th>
                      </tr>
                    </Thead>
                    <tbody>
                      {questionRows.map(row => (
                        <Tr
                          key={row.key}
                          style={row.isDisabled ? { opacity: 0.5, pointerEvents: "none", background: "rgba(0,0,0,0.03)" } : undefined}
                        >
                          <Td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={selectedKeys.has(row.key)}
                              onChange={() => toggleKey(row.key)}
                              disabled={row.isDisabled}
                              style={{ cursor: row.isDisabled ? "not-allowed" : "pointer", accentColor: "#3f88a5" }}
                            />
                          </Td>
                          <Td>
                            {row.q.title}
                            {row.hasExisting && <ExistingBadge title="Trigger set for the selected period — editing allowed">existing</ExistingBadge>}
                            {row.isDisabled && <ExistingBadge title="A trigger already exists for this KPI in a different period" style={{ background: "#e57373", color: "#fff" }}>locked</ExistingBadge>}
                          </Td>
                          <Td>{resolveBaseUnit(row.q) || "—"}</Td>
                          <Td>
                            <InlineInput
                              type="number"
                              placeholder="Min"
                              value={getRowMin(row.key, row)}
                              onChange={e => handleRowChange(row.key, "min", e.target.value)}
                              disabled={row.isDisabled}
                              style={{ minWidth: 90 }}
                            />
                          </Td>
                          <Td>
                            <InlineInput
                              type="number"
                              placeholder="Max"
                              value={getRowMax(row.key, row)}
                              onChange={e => handleRowChange(row.key, "max", e.target.value)}
                              disabled={row.isDisabled}
                              style={{ minWidth: 90 }}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </PreviewTable>
                </ScrollableTable>
              )}
            </>
          )}

        </FormCard>
      )}
    </Wrapper>
  );
};

export default ConfigureTab;
